import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import AppContext from "../../../../context/Context";
import { Table, Tabs, Badge, Button, Popover, Tag, Modal, message, Tooltip, Select, Input, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { List, BarChart3, Settings2, Activity, Info, Bolt, Filter, RefreshCw } from "lucide-react";
import StrategyDataTabs from "./StrategyDataTabs";
import StrategyRiskModal from "../StrategyRiskModal";
import "../QuiktradeAlgo.css";
import "./index.css";
import PnlMtmModal from "../../../common/PnlMtmModal";
import { getSignalTypeLabel, StatusBadge, SignalNamesDisplay, BrokerAccountsDisplay, getBrokerLabelUtil } from '../utils'
import { fmtINR } from "../../../../util/helper";
import { getBasketPositions, getStrategies, deleteStrategy, squareOffAllPositions, stopAndExitStrategy, delinkStrategySignal, linkStrategySignal } from "../../../../api/strategyApi";
import { getTicksData } from "../../../../redux/actions/optionChain";
import SubscriptionCheckModal from "components/common/SubscriptionCheckModal";
import { useToast } from "components/common/custom-toast/Toast";
import LinkedSignalsModal from "../LinkedSignalsModal";

const { confirm } = Modal;

const ListStrategies = () => {
  const {
    config: { isDark },
  } = useContext(AppContext);
  const isDarkRef = useRef(isDark);

  useEffect(() => {
    isDarkRef.current = isDark;
  }, [isDark]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [sockSource, setSockSource] = useState(null);

  // Modal states
  const [isStrategyRiskModalOpen, setIsStrategyRiskModalOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedStrategyCombPrem, setSelectedStrategyCombPrem] = useState(0);
  const [selectedStrategyCalcVal, setSelectedStrategyCalcVal] = useState(0);
  const [isPnlModalOpen, setIsPnlModalOpen] = useState(false);
  const [pnlModalConfig, setPnlModalConfig] = useState({ title: "PNL / MTM", defaultLevel: "strategy", brokerId: null, brokerName: null, baskets: [], positions: [] });
  const [isLinkedSignalsModalOpen, setIsLinkedSignalsModalOpen] = useState(false);
  const [modalSignalLinks, setModalSignalLinks] = useState([]);
  const [modalStrategyName, setModalStrategyName] = useState("");
  const [filters, setFilters] = useState({
    strategyName: "",
    account: null,
    profitLoss: null,
    status: null,
    intraPos: null,
    linkedSignal: null,
    activeStrategies: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const brokersList = useSelector((s) => s?.brokers?.list || []);

  const isLoggedIn = useSelector((s) => s?.user?.isLoggedIn === true);
  const brokerLoggedIn = useSelector((s) => s?.user?.brokerLoggedIn === true);
  const user = useSelector((s) => s?.user);
  const filter = useSelector((s) => s?.filter);
  const socketJWT = useSelector((s) => s.socketJwt?.data);
  const tradeFeedSource = useSelector((s) => s.tradeFeedSource?.source);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const info = useSelector((s) => s.filter);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const accessiblePlans = useSelector((state) => state?.packages?.accessiblePlans);
  const plansLoading = accessiblePlans === undefined;
  const hasAlgoAccess = accessiblePlans?.length > 0 ? accessiblePlans?.some(planName =>
    planName === 'QT-Unlimited' || planName === 'QT-Algo' || planName === 'trial'
  ) : false;
  const toast = useToast()

  const getBrokerLabel = (idOrObj) => getBrokerLabelUtil(idOrObj, brokersList);


  const stopStrategy = async (strategyId) => {
    try {
      const res = await stopAndExitStrategy(strategyId)

      if (res.code === 200) {
        toast.success('Strategy stopped successfully');
      } else {
        throw new Error('Failed to stop strategy');
      }
    } catch (error) {
      console.error('Error stopping strategy:', error);
      toast.error('Failed to stop strategy');
    }
  };

  const squareOffStrategy = async (strategyId) => {
    try {
      const res = await squareOffAllPositions(strategyId)

      if (res?.code === 200) {
        toast.success('Strategy squared off successfully');
      } else {
        throw new Error('Failed to square off strategy');
      }
    } catch (error) {
      console.error('Error squaring off strategy:', error);
      toast.error('Failed to square off strategy');
    }
  };

  const handleStopAndExit = (record) => {
    confirm({
      title: 'Square off & Pause',
      icon: null,
      content: `Are you sure you want to square off & pause the strategy "${record.name}"? This action cannot be undone.`,
      okText: 'Yes, Square off & Pause',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`stop_${record.id}`]: true }));
        await stopStrategy(record.id);
        setActionLoading(prev => ({ ...prev, [`stop_${record.id}`]: false }));
      },
    });
  };

  const handleSquareOffAll = (record) => {
    confirm({
      title: 'Square Off All Positions',
      icon: null,
      content: `Are you sure you want to square off all positions for the strategy "${record.name}"?`,
      okText: 'Yes, Square Off All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`squareoff_${record.id}`]: true }));
        await squareOffStrategy(record.id);
        setActionLoading(prev => ({ ...prev, [`squareoff_${record.id}`]: false }));
      },
    });
  };

  const handleDeleteStrategy = async (record) => {
    try {
      const res = await deleteStrategy(record.id);
      if (res?.code === 200) {
        toast.success('Strategy deleted');
        const str = await getStrategies();
        if (str?.code === 200) {
          setRows(Array.isArray(str?.data) ? str.data : []);
        }
      } else if (res?.sessionExpired) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(res?.error || 'Failed to delete');
      }
    } catch (e) {
      console.error("Error:" + e)
    }
  }
  const handleDeleteConfirm = (record) => {
    confirm({
      title: 'Delete Strategy',
      icon: null,
      content: `Are you sure you want to delete the strategy "${record.name}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(prev => ({ ...prev, [`delete_${record.id}`]: true }));
        await handleDeleteStrategy(record);
        setActionLoading(prev => ({ ...prev, [`delete_${record.id}`]: false }));
      },
    });
  };

  const handleToggleSignalLink = async (record) => {
    const isDelinked = record.isSignalDelinked;
    const action = isDelinked ? 'relink' : 'delink';
    const strategyId = record.id;

    confirm({
      title: isDelinked ? 'Relink Signals' : 'Delink Signals',
      content: `Are you sure you want to ${action} signals for strategy "${record.name}"?`,
      okText: isDelinked ? 'Relink' : 'Delink',
      okType: isDelinked ? 'primary' : 'danger',
      icon: null,
      onOk: async () => {
        try {
          setActionLoading(prev => ({ ...prev, [`signal_${strategyId}`]: true }));
          const res = isDelinked ? await linkStrategySignal(strategyId) : await delinkStrategySignal(strategyId);
          if (res?.code === 200) {
            toast.success(`Signals ${isDelinked ? 'relinked' : 'delinked'} successfully`);
            const str = await getStrategies();
            if (str?.code === 200) {
              setRows(Array.isArray(str?.data) ? str.data : []);
            }
          } else {
            toast.error(res?.error || `Failed to ${action} signals`);
          }
        } catch (error) {
          console.error(`Error ${action} signals:`, error);
          toast.error(`Failed to ${action} signals`);
        } finally {
          setActionLoading(prev => ({ ...prev, [`signal_${strategyId}`]: false }));
        }
      }
    });
  };

  const handleOpenStrategyRiskModal = async (record) => {
    setSelectedStrategy(record);

    let combPrem = 0;
    let calcVal = 0;
    try {
      const firstBrokerId = Array.isArray(record?.brokers) && record.brokers.length
        ? (record.brokers[0]?.id || record.brokers[0])
        : null;

      if (firstBrokerId && record?.basketId?.[firstBrokerId]) {
        const basketId = record.basketId[firstBrokerId];

        const positionsRes = await getBasketPositions(basketId);
        if (positionsRes?.code === 200 && Array.isArray(positionsRes?.data)) {
          const positions = positionsRes.data || [];

          const tokens = positions.map(p => p?.instrumentToken).filter(Boolean);
          let ticks = {};
          if (tokens.length > 0) {
            try {
              ticks = await getTicksData(tokens);
            } catch (e) {
              console.error('Error fetching ticks:', e);
            }
          }

          let buyPremium = 0;
          let sellPremium = 0;
          const usePerLot = !!record?.rms?.perLot;
          for (let i = 0; i < positions.length; i++) {
            const leg = positions[i] || {};
            const netQty = parseFloat(leg.netQuantity || 0);
            const qty = Math.abs(netQty || parseFloat(leg.quantity || 0) || 0);
            const lotSize = parseFloat(leg.instrument?.lotSize || leg.lotSize || 1);
            const lots = lotSize > 0 ? qty / lotSize : qty;
            const ltp = Number(ticks?.[leg.instrumentToken]?.lp || leg.ltp || 0);
            const side = leg.transactionType
              ? String(leg.transactionType).toUpperCase()
              : (netQty < 0 ? "SELL" : netQty > 0 ? "BUY" : null);

            if (lots > 0 && side) {
              if (usePerLot) {
                if (side === "BUY") {
                  buyPremium += ltp;
                } else if (side === "SELL") {
                  sellPremium += ltp;
                }
              } else {
                if (side === "BUY") {
                  buyPremium += ltp * lots;
                } else if (side === "SELL") {
                  sellPremium += ltp * lots;
                }
              }
            }
          }
          combPrem = buyPremium - sellPremium;
          calcVal = buyPremium - sellPremium;
        }
      }
    } catch (error) {
      console.error('Error calculating combined premium:', error);
      combPrem = 0;
      calcVal = 0;
    }

    setSelectedStrategyCombPrem(combPrem);
    setSelectedStrategyCalcVal(calcVal);
    setIsStrategyRiskModalOpen(true);
  };


  const isBrokersLoading = useSelector((state) => state?.brokers?.isLoading);
  const isPlansLoading = useSelector((state) => state?.packages?.isLoading);

  useEffect(() => {
    if (isBrokersLoading || isPlansLoading) {
      return;
    }

    if (!isLoggedIn) {
      toast.error("Please login first");
      navigate("/authentication/login");
      return;
    }

    if (hasAlgoAccess === false) {
      setShowSubscriptionModal(true);
      return;
    }
    if (!brokerLoggedIn) {
      toast.error("Please connect your broker account");
      navigate("/broker-login");
      return;
    }

    const purposed = (brokersList || []).filter(b => (b.purpose === 'algo' || b.type === 'papertrading'));
    if (purposed.length === 0) {
      toast.error("Please use broker account with purpose 'algo' to access Algo signals");
      navigate("/broker-login");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const res = await getStrategies();
        if (res?.code === 200) {
          setRows(Array.isArray(res?.data) ? res.data : []);
        } else if (res?.sessionExpired) {
          toast.error("Session expired. Please login again.");
          navigate("/authentication/login");
        } else {
          toast.error(res?.error || "Failed to load strategies");
        }
      } catch (e) {
        toast.error("Failed to load strategies");
      } finally {
        setLoading(false);
      }
    })();
  }, [isLoggedIn, brokerLoggedIn, hasAlgoAccess, isPlansLoading, isBrokersLoading]);


  useEffect(() => {
    if (socketJWT && Object.keys(socketJWT).length > 0 && tradeFeedSource) {
      let src = tradeFeedSource;
      const mtmChannel = `${socketJWT.wsId}`;
      const mtmSub = src
        .subscribe(mtmChannel, (message) => {
          try {
            const payload = message?.data;
            if (!payload || payload?.channel !== 'strategy_mtm') return;
            const mtm = payload?.mtm;
            if (!mtm || typeof mtm.id === 'undefined') return;
            const el = document.getElementById(`pnl-${mtm.id}`);
            if (el) {
              const pnl = Number(mtm?.total_pnl ?? 0);
              el.textContent = fmtINR(pnl.toFixed(2));
              el.style.color = pnl === 0 ? '' : (pnl > 0 ? (isDarkRef.current ? '#00d27a' : '#16a34a') : (isDarkRef.current ? '#F87171' : '#E74C3C'));
            }

            const u = Number(mtm?.unrealised_pnl ?? 0);
            const r = Number(mtm?.realised_pnl ?? 0);
            const uEl = document.getElementById(`upnl-${mtm.id}`);
            if (uEl) {
              uEl.textContent = fmtINR(u.toFixed(2));
              uEl.style.color = u === 0 ? '' : (u > 0 ? (isDarkRef.current ? '#00d27a' : '#16a34a') : (isDarkRef.current ? '#F87171' : '#E74C3C'));
            }
            const rEl = document.getElementById(`rpnl-${mtm.id}`);
            if (rEl) {
              rEl.textContent = fmtINR(r.toFixed(2));
              rEl.style.color = r === 0 ? '' : (r > 0 ? (isDarkRef.current ? '#00d27a' : '#16a34a') : (isDarkRef.current ? '#F87171' : '#E74C3C'));
            }
          } catch (e) {
            // no-op
          }
        })
        .on('subscribe', () => {
          // subscribed
        })
        .on('error', (e) => console.error('strategy_mtm error', e));

      return () => {
        try { mtmSub?.unsubscribe(); } catch { }
      };
    }
  }, [socketJWT, tradeFeedSource, info?.broker?.id, dispatch]);

  const columns = useMemo(
    () => [
      {
        title: "Strategy Name", align: "left", dataIndex: "name", key: "name",
        render: (name, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{name}</span>
            {record.description && (
              <Tooltip title={record.description} placement="top">
                <Info size={14} style={{ color: '#3b82f6', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </div>
        )
      },
      {
        title: "Symbol",
        align: "left",
        dataIndex: ["underlyingInstrument", "underlying"],
        key: "symbol",
        render: (v) => <span className="symbol-badge">{v}</span>
      },
      {
        title: "PNL",
        dataIndex: "totalPnl",
        align: "right",
        key: "pnl",
        render: (v, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: "flex-end", whiteSpace: "nowrap" }}>
            <p id={`pnl-${record.id}`} style={{
              color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')),
              width: "14ch",
              fontSize: "12px"
            }}>
              {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
            </p>
            <Tooltip title="PNL Graph">
              <Button
                type="text"
                size="small"
                icon={<BarChart3 className="pnl-graph" />}
                onClick={() => {
                  try {
                    setPnlModalConfig({
                      title: `PNL / MTM - ${record.name}`,
                      defaultLevel: 'strategy',
                      brokerId: null,
                      brokerName: null,
                      strategyId: record.id,
                      baskets: [],
                      positions: [],
                    });
                    setIsPnlModalOpen(true);
                  } catch (e) { }
                }}
              />
            </Tooltip>
          </div>
        )
      },
      {
        title: "Unrealized",
        dataIndex: "unrealisedPnl",
        align: "right",
        key: "upnl",
        render: (v, record) => (
          <p id={`upnl-${record.id}`} style={{ color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), width: "14ch", fontSize: "12px" }}>
            {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
          </p>
        )
      },
      {
        title: "Realized",
        dataIndex: "realisedPnl",
        align: "right",
        key: "rpnl",
        render: (v, record) => (
          <p id={`rpnl-${record.id}`} style={{ color: Number(v) === 0 ? '' : (Number(v) > 0 ? (isDark ? '#00d27a' : '#16a34a') : (isDark ? '#F87171' : '#E74C3C')), width: "14ch", fontSize: "12px" }}>
            {Number(v) > 0 ? `+${fmtINR(Number(v || 0).toFixed(2))}` : fmtINR(Number(v || 0).toFixed(2))}
          </p>
        )
      },
      {
        title: "Linked Accounts",
        align: "left",
        dataIndex: "brokers",
        key: "brokers",
        render: (brokers) => <BrokerAccountsDisplay brokers={Array.isArray(brokers) ? brokers : []} labelFn={getBrokerLabel} />
      },
      {
        title: "Linked Signals",
        align: "center",
        dataIndex: "signalLinks",
        key: "signal",
        render: (signalLinks, record) => (
          <SignalNamesDisplay
            signalLinks={signalLinks}
            onIconClick={(links) => {
              setModalSignalLinks(links);
              setModalStrategyName(record?.name || "");
              setIsLinkedSignalsModalOpen(true);
            }}
          />
        )
      },
      {
        title: "Status",
        align: "left",
        dataIndex: "status",
        key: "status",
        render: (s) => <StatusBadge status={s} />
      },
      { title: "Intra / Pos", align: "c", dataIndex: "positionalOrIntraday", key: "ip", render: (value) => (value ? "Positional" : "Intraday") },
      {
        title: "Risk Settings", align: "center", key: "risk", render: (_, record) => (
          <Bolt style={{ cursor: 'pointer' }} strokeWidth={1.5} size={18} onClick={() => handleOpenStrategyRiskModal(record)} />
        )
      },
      {
        title: "Actions",
        align: "center",
        key: "actions",
        render: (_, record) => (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              size="small"
              loading={actionLoading[`stop_${record.id}`]}
              onClick={() => handleStopAndExit(record)}
              className="square-off-pause-btn"
            >
              Square off & Pause
            </Button>
            <Button
              size="small"
              loading={actionLoading[`squareoff_${record.id}`]}
              onClick={() => handleSquareOffAll(record)}
              className="square-off-btn"
            >
              Square off All
            </Button>
            <Button
              size="small"
              loading={actionLoading[`signal_${record.id}`]}
              onClick={() => handleToggleSignalLink(record)}
              className="signal-link-btn"
            >
              {record.isSignalDelinked ? 'Relink Signal' : 'Delink Signal'}
            </Button>
            <Button className="edit-strat-btn" onClick={() => navigate(`/algo/edit-strategy/${record.id}`)} size="small">
              Edit
            </Button>
            <Button
              size="small"
              loading={actionLoading[`delete_${record.id}`]}
              onClick={() => handleDeleteConfirm(record)}
              className="delete-strat-btn"
            >
              Delete
            </Button>
          </div>
        )
      },
    ],
    [actionLoading, brokersList, isDark]
  );

  const expandedRowRender = (record) => {
    return (
      <StrategyDataTabs
        strategyId={record.id}
        user={user}
        socketJWT={socketJWT}
        source={tradeFeedSource}
        strategies={rows}
      />
    );
  };

  // Filter functions
  const getUniqueSignals = () => {
    const signals = new Set();
    rows.forEach(strategy => {
      if (Array.isArray(strategy.signalLinks)) {
        strategy.signalLinks.forEach(signal => {
          if (signal?.name) {
            signals.add(signal.name);
          }
        });
      }
    });
    return Array.from(signals).sort();
  };

  const getUniqueAccounts = () => {
    const accounts = new Set();
    rows.forEach(strategy => {
      if (Array.isArray(strategy.brokers)) {
        strategy.brokers.forEach(broker => {
          const brokerId = typeof broker === 'object' ? broker.id : broker;
          accounts.add(String(brokerId));
        });
      }
    });
    return Array.from(accounts);
  };

  const applyFilters = (strategies) => {
    let filtered = [...strategies];

    if (filters.strategyName) {
      const searchTerm = filters.strategyName.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.account) {
      filtered = filtered.filter(s => {
        if (!Array.isArray(s.brokers)) return false;
        return s.brokers.some(broker => {
          const brokerId = typeof broker === 'object' ? broker.id : broker;
          return String(brokerId) === String(filters.account);
        });
      });
    }

    if (filters.profitLoss === 'profit') {
      filtered = filtered.filter(s => Number(s.totalPnl || 0) > 0);
    } else if (filters.profitLoss === 'loss') {
      filtered = filtered.filter(s => Number(s.totalPnl || 0) < 0);
    }

    if (filters.status !== null) {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    if (filters.intraPos === 'intraday') {
      filtered = filtered.filter(s => s.positionalOrIntraday === false);
    } else if (filters.intraPos === 'positional') {
      filtered = filtered.filter(s => s.positionalOrIntraday === true);
    }

    if (filters.linkedSignal) {
      filtered = filtered.filter(s => {
        if (!Array.isArray(s.signalLinks)) return false;
        return s.signalLinks.some(signal => signal?.name === filters.linkedSignal);
      });
    }

    if (filters.activeStrategies) {
      filtered = filtered.filter(s => Number(s.totalPnl || 0) !== 0);
    }

    return filtered;
  };

  const filteredRows = useMemo(() => {
    return applyFilters(rows);
  }, [rows, filters]);

  const clearFilters = () => {
    setFilters({
      strategyName: "",
      account: null,
      profitLoss: null,
      status: null,
      intraPos: null,
      linkedSignal: null,
      activeStrategies: false
    });
  };

  const hasActiveFilters = filters.strategyName || filters.account ||
    filters.profitLoss || filters.status !== null || filters.intraPos ||
    filters.linkedSignal || filters.activeStrategies;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await getStrategies();
      if (res?.code === 200) {
        setRows(Array.isArray(res?.data) ? res.data : []);
        toast.success('Strategies refreshed successfully');
      } else if (res?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate("/authentication/login");
      } else {
        toast.error(res?.error || "Failed to refresh strategies");
      }
    } catch (e) {
      toast.error("Failed to refresh strategies");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="quiktrade-algo">
      <div className="algo-content">
        <div className="create-strategy-container">
          <div className="config-section">
            <div style={{ justifyContent: "space-between", flexWrap: "wrap", alignItems: "start" }} className="qt-algo-section-header">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Activity size={20} />
                <h3>Strategy View</h3>
              </div>
              <div style={{
                display: "flex",
                gap: '12px',
                flexWrap: 'wrap'
              }}>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Strategy Name
                  </label>
                  <Input
                    placeholder="Search strategies..."
                    value={filters.strategyName}
                    onChange={(e) => setFilters(prev => ({ ...prev, strategyName: e.target.value }))}
                    allowClear
                    size="small"
                    style={{ height: "32px", width: '130px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Account
                  </label>
                  <Select
                    style={{ width: '130px' }}
                    placeholder="All Accounts"
                    value={filters.account}
                    onChange={(val) => setFilters(prev => ({ ...prev, account: val }))}
                    allowClear
                  >
                    {getUniqueAccounts().map(brokerId => (
                      <Select.Option key={brokerId} value={brokerId}>
                        {getBrokerLabel(brokerId)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Profit/Loss
                  </label>
                  <Select
                    style={{ width: '130px' }}
                    placeholder="All"
                    value={filters.profitLoss}
                    onChange={(val) => setFilters(prev => ({ ...prev, profitLoss: val }))}
                    allowClear
                  >
                    <Select.Option value="profit">Profit Making</Select.Option>
                    <Select.Option value="loss">Loss Making</Select.Option>
                  </Select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Status
                  </label>
                  <Select
                    style={{ width: '110px' }}
                    placeholder="All Status"
                    value={filters.status}
                    onChange={(val) => setFilters(prev => ({ ...prev, status: val === undefined ? null : val }))}
                    allowClear
                  >
                    <Select.Option value={0}>Stopped</Select.Option>
                    <Select.Option value={1}>Monitoring</Select.Option>
                    <Select.Option value={2}>Active</Select.Option>
                  </Select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Type
                  </label>
                  <Select
                    style={{ width: '100px' }}
                    placeholder="All Types"
                    value={filters.intraPos}
                    onChange={(val) => setFilters(prev => ({ ...prev, intraPos: val }))}
                    allowClear
                  >
                    <Select.Option value="intraday">Intraday</Select.Option>
                    <Select.Option value="positional">Positional</Select.Option>
                  </Select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Linked Signal
                  </label>
                  <Select
                    style={{ width: '130px' }}
                    placeholder="All Signals"
                    value={filters.linkedSignal}
                    onChange={(val) => setFilters(prev => ({ ...prev, linkedSignal: val }))}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {getUniqueSignals().map(signal => (
                      <Select.Option key={signal} value={signal}>
                        {signal}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: 500 }}>
                    Active Strategies
                  </label>
                  <Switch
                    checked={filters.activeStrategies}
                    onChange={(checked) => setFilters(prev => ({ ...prev, activeStrategies: checked }))}
                    size="small"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'end', alignSelf: 'flex-end' }}>
                  <Button
                    type="default"
                    onClick={clearFilters}
                  >
                    Clear All
                  </Button>
                  <Button
                    icon={<RefreshCw size={16} />}
                    onClick={handleRefresh}
                    loading={refreshing}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
            <Table
              rowKey={(r) => r.id}
              loading={loading}
              dataSource={filteredRows}
              columns={columns}
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpandedRowsChange: setExpandedRowKeys,
              }}
              pagination={false}
              scroll={{ x: true }}
              size="small"
            />
          </div>
        </div>
      </div>

      {/* Strategy Risk Modal */}
      {isStrategyRiskModalOpen && selectedStrategy && (
        <StrategyRiskModal
          show={isStrategyRiskModalOpen}
          setShow={setIsStrategyRiskModalOpen}
          strategyData={selectedStrategy}
          combPrem={selectedStrategyCombPrem}
          calcVal={selectedStrategyCalcVal}
          onSuccess={() => {
            const refreshStrategies = async () => {
              try {
                const res = await getStrategies();
                if (res?.code === 200) {
                  setRows(Array.isArray(res?.data) ? res.data : []);
                }
              } catch (error) {
                console.error('Error refreshing strategies:', error);
              }
            };
            refreshStrategies();
          }}
        />
      )}

      {isPnlModalOpen && (
        <PnlMtmModal
          show={isPnlModalOpen}
          onHide={() => setIsPnlModalOpen(false)}
          title={pnlModalConfig.title}
          defaultLevel={pnlModalConfig.defaultLevel}
          brokerId={pnlModalConfig.brokerId}
          brokerName={pnlModalConfig.brokerName}
          strategyId={pnlModalConfig.strategyId}
          baskets={pnlModalConfig.baskets}
          positions={pnlModalConfig.positions}
          selection={false}
        />
      )}

      <SubscriptionCheckModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        type="Algo"
      />

      <LinkedSignalsModal
        open={isLinkedSignalsModalOpen}
        onCancel={() => setIsLinkedSignalsModalOpen(false)}
        signalLinks={modalSignalLinks}
        strategyName={modalStrategyName}
      />
    </div>
  );
};

export default ListStrategies;